import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Plus, Trash2, Save, Eye, Copy, Check, Zap, Calendar, Clock, Loader2, ExternalLink, MessageCircle, HelpCircle, X, Smartphone, Bell } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { agendaTemplates, defaultWorkingHours, weekDays, formatDuration, formatPrice } from '../data/agendaMock';
import { siteData } from '../data/mock';
import MasterAccess from '../components/MasterAccess';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const generateSlug = (name) => {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const AgendaConfigurar = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    phone: '',
    email: '',
    cpfCnpj: '',
    address: '',
    template: null,
    services: [],
    workingHours: { ...defaultWorkingHours },
    reminderMode: '', // 'automatic' ou 'manual'
    zapiInstanceId: '',
    zapiToken: '',
    skipZapi: false
  });

  const [newService, setNewService] = useState({ name: '', duration: 30, price: '' });

  const slug = formData.business ? generateSlug(formData.business) : 'seu-negocio';
  const fullUrl = `${window.location.origin}/agenda/${slug}`;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTemplateSelect = (template) => {
    setFormData({
      ...formData,
      template: template.id,
      services: template.id === 'personalizado' ? [] : [...template.services]
    });
  };

  const handleAddService = () => {
    if (newService.name && newService.price) {
      setFormData({
        ...formData,
        services: [
          ...formData.services,
          {
            id: Date.now(),
            name: newService.name,
            duration: parseInt(newService.duration) || 30,
            price: parseFloat(newService.price)
          }
        ]
      });
      setNewService({ name: '', duration: 30, price: '' });
    }
  };

  const handleRemoveService = (id) => {
    setFormData({
      ...formData,
      services: formData.services.filter(s => s.id !== id)
    });
  };

  const handleWorkingHoursChange = (day, field, value) => {
    setFormData({
      ...formData,
      workingHours: {
        ...formData.workingHours,
        [day]: {
          ...formData.workingHours[day],
          [field]: value
        }
      }
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCreatePaymentLink = async () => {
    setLoading(true);
    setPaymentError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/asaas/create-subscription`, {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        customer_cpfCnpj: formData.cpfCnpj,
        plan_name: "Agenda Cheia",
        plan_value: 49.99,
        billing_type: "UNDEFINED",
        cycle: "MONTHLY",
        description: `Agenda Cheia - ${formData.business}`,
        business_name: formData.business,
        slug: slug
      });
      
      if (response.data.success && response.data.payment_link) {
        setPaymentLink(response.data.payment_link);
      } else {
        setPaymentError(response.data.error || 'Erro ao gerar link de pagamento');
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      setPaymentError('Erro ao conectar com o sistema de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    
    // Preparar dados para o backend
    const professionalData = {
      name: formData.name,
      business: formData.business,
      phone: formData.phone,
      email: formData.email,
      cpf_cnpj: formData.cpfCnpj,
      address: formData.address || '',
      template: formData.template?.id || null,
      services: formData.services.map(s => ({
        id: String(s.id),
        name: s.name,
        duration: s.duration,
        price: s.price
      })),
      working_hours: formData.workingHours,
      reminder_mode: formData.reminderMode || 'manual',
      zapi_instance_id: formData.zapiInstanceId || null,
      zapi_token: formData.zapiToken || null
    };

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/agenda/professionals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(professionalData)
      });

      const result = await response.json();
      if (response.ok) {
        console.log('Profissional salvo no backend:', result);
      } else {
        console.error('Erro do backend:', result);
      }
    } catch (error) {
      console.error('Erro ao salvar no backend:', error);
    }

    // Também salvar localmente como backup
    const professional = {
      ...formData,
      id: Date.now().toString(),
      slug,
      createdAt: new Date().toISOString()
    };
    localStorage.setItem(`agenda_${slug}`, JSON.stringify(professional));
    
    setLoading(false);
    setStep(6);
  };

  const handleOpenSupport = () => {
    const message = `Olá! Preciso de ajuda para configurar o Z-API na minha Agenda Cheia.\n\nNome: ${formData.name}\nNegócio: ${formData.business}\nEmail: ${formData.email}`;
    window.open(`https://wa.me/5524993184232?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Step 1: Dados básicos
  const renderStep1 = () => (
    <div className="max-w-xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Seus dados</h2>
      <p className="text-slate-600 mb-8">Informações do seu negócio</p>
      
      <div className="space-y-6">
        <div>
          <Label htmlFor="name">Seu nome</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="João Silva"
            className="mt-2"
          />
        </div>
        
        <div>
          <Label htmlFor="business">Nome do negócio</Label>
          <Input
            id="business"
            name="business"
            value={formData.business}
            onChange={handleInputChange}
            placeholder="Barbearia do João"
            className="mt-2"
          />
          {formData.business && (
            <p className="text-sm text-slate-500 mt-2">
              Seu link: <span className="text-blue-600 font-medium">/agenda/{slug}</span>
            </p>
          )}
        </div>
        
        <div>
          <Label htmlFor="phone">WhatsApp</Label>
          <Input
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="(24) 99999-9999"
            className="mt-2"
          />
        </div>
        
        <div>
          <Label htmlFor="email">E-mail</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="joao@email.com"
            className="mt-2"
          />
        </div>
        
        <div>
          <Label htmlFor="cpfCnpj">CPF ou CNPJ</Label>
          <Input
            id="cpfCnpj"
            name="cpfCnpj"
            value={formData.cpfCnpj}
            onChange={handleInputChange}
            placeholder="000.000.000-00"
            className="mt-2"
          />
        </div>
        
        <div>
          <Label htmlFor="address">Endereço (opcional)</Label>
          <Input
            id="address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            placeholder="Rua Principal, 123 - Centro"
            className="mt-2"
          />
        </div>
      </div>
      
      <div className="mt-8 flex justify-end">
        <Button
          onClick={() => setStep(2)}
          disabled={!formData.name || !formData.business || !formData.phone}
          className="btn-primary"
        >
          Próximo
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );

  // Step 2: Escolher template
  const renderStep2 = () => (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Tipo de negócio</h2>
      <p className="text-slate-600 mb-8">Escolha um modelo ou personalize</p>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {agendaTemplates.map((template) => {
          const Icon = LucideIcons[template.icon] || LucideIcons.Settings;
          const isSelected = formData.template === template.id;
          
          return (
            <button
              key={template.id}
              onClick={() => handleTemplateSelect(template)}
              className={`p-6 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-blue-300 bg-white'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                isSelected ? 'bg-blue-100' : 'bg-slate-100'
              }`}>
                <Icon size={24} className={isSelected ? 'text-blue-600' : 'text-slate-600'} />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{template.name}</h3>
              <p className="text-sm text-slate-500">{template.description}</p>
              {template.services.length > 0 && (
                <p className="text-xs text-blue-600 mt-2">
                  {template.services.length} serviços inclusos
                </p>
              )}
            </button>
          );
        })}
      </div>
      
      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={() => setStep(1)}>
          <ArrowLeft size={18} />
          Voltar
        </Button>
        <Button
          onClick={() => setStep(3)}
          disabled={!formData.template}
          className="btn-primary"
        >
          Próximo
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );

  // Step 3: Configurar serviços
  const renderStep3 = () => (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Configure seus serviços</h2>
      <p className="text-slate-600 mb-8">Ajuste os serviços ou adicione novos</p>
      
      <div className="bg-slate-50 rounded-xl p-6 mb-6">
        <div className="space-y-3">
          {formData.services.map((service) => (
            <div
              key={service.id}
              className="flex items-center gap-4 bg-white p-4 rounded-lg border border-slate-200"
            >
              <div className="flex-1">
                <p className="font-medium text-slate-800">{service.name}</p>
                <p className="text-sm text-slate-500">
                  {formatDuration(service.duration)} • {formatPrice(service.price)}
                </p>
              </div>
              <button
                onClick={() => handleRemoveService(service.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
          
          {formData.services.length === 0 && (
            <p className="text-center text-slate-500 py-8">Nenhum serviço cadastrado</p>
          )}
        </div>
      </div>
      
      {/* Adicionar novo serviço */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 mb-8">
        <h3 className="font-semibold text-slate-900 mb-4">Adicionar serviço</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <Input
              placeholder="Nome do serviço"
              value={newService.name}
              onChange={(e) => setNewService({ ...newService, name: e.target.value })}
            />
          </div>
          <div>
            <select
              value={newService.duration}
              onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-input bg-background"
            >
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
              <option value={45}>45 min</option>
              <option value={60}>1 hora</option>
              <option value={90}>1h 30min</option>
              <option value={120}>2 horas</option>
              <option value={180}>3 horas</option>
            </select>
          </div>
          <div>
            <Input
              type="number"
              placeholder="Preço"
              value={newService.price}
              onChange={(e) => setNewService({ ...newService, price: e.target.value })}
            />
          </div>
        </div>
        <Button
          onClick={handleAddService}
          variant="outline"
          className="mt-4"
          disabled={!newService.name || !newService.price}
        >
          <Plus size={18} />
          Adicionar
        </Button>
      </div>
      
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(2)}>
          <ArrowLeft size={18} />
          Voltar
        </Button>
        <Button
          onClick={() => setStep(4)}
          disabled={formData.services.length === 0}
          className="btn-primary"
        >
          Próximo
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );

  // Step 4: Horários de funcionamento
  const renderStep4 = () => (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Horários de funcionamento</h2>
      <p className="text-slate-600 mb-8">Configure quando você atende</p>
      
      <div className="space-y-4">
        {weekDays.map((day) => {
          const dayConfig = formData.workingHours[day.key];
          return (
            <div
              key={day.key}
              className={`p-4 rounded-xl border transition-all ${
                dayConfig.enabled ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-slate-900">{day.label}</span>
                <Switch
                  checked={dayConfig.enabled}
                  onCheckedChange={(checked) => handleWorkingHoursChange(day.key, 'enabled', checked)}
                />
              </div>
              
              {dayConfig.enabled && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Início</Label>
                    <Input
                      type="time"
                      value={dayConfig.start || '09:00'}
                      onChange={(e) => handleWorkingHoursChange(day.key, 'start', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Fim</Label>
                    <Input
                      type="time"
                      value={dayConfig.end || '18:00'}
                      onChange={(e) => handleWorkingHoursChange(day.key, 'end', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Intervalo início</Label>
                    <Input
                      type="time"
                      value={dayConfig.break_start || ''}
                      onChange={(e) => handleWorkingHoursChange(day.key, 'break_start', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Intervalo fim</Label>
                    <Input
                      type="time"
                      value={dayConfig.break_end || ''}
                      onChange={(e) => handleWorkingHoursChange(day.key, 'break_end', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={() => setStep(3)}>
          <ArrowLeft size={18} />
          Voltar
        </Button>
        <Button onClick={() => setStep(5)} className="btn-primary">
          Próximo
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );

  // Step 5: Integração WhatsApp (Z-API)
  const renderStep5 = () => (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Lembretes no WhatsApp</h2>
      <p className="text-slate-600 mb-8">Escolha como quer enviar lembretes para seus clientes</p>
      
      {/* Escolha do modo */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {/* Opção Automático */}
        <button
          onClick={() => setFormData({ ...formData, reminderMode: 'automatic', skipZapi: false })}
          className={`p-6 rounded-2xl border-2 text-left transition-all ${
            formData.reminderMode === 'automatic'
              ? 'border-green-500 bg-green-50'
              : 'border-slate-200 hover:border-green-300 bg-white'
          }`}
        >
          <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
            <Zap size={24} className={formData.reminderMode === 'automatic' ? 'text-green-600' : 'text-green-500'} />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">100% Automático</h3>
          <p className="text-sm text-slate-600 mb-3">
            O sistema envia os lembretes sozinho. Você não precisa fazer nada.
          </p>
          <div className="flex items-center gap-2 text-sm">
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
              +R$ 99/mês (Z-API)
            </span>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            <li className="flex items-center gap-2">
              <Check size={14} className="text-green-500" /> Lembrete 24h antes
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} className="text-green-500" /> Lembrete 2h antes
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} className="text-green-500" /> Zero trabalho manual
            </li>
          </ul>
        </button>

        {/* Opção Semi-automático */}
        <button
          onClick={() => setFormData({ ...formData, reminderMode: 'manual', skipZapi: true, zapiInstanceId: '', zapiToken: '' })}
          className={`p-6 rounded-2xl border-2 text-left transition-all ${
            formData.reminderMode === 'manual'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 hover:border-blue-300 bg-white'
          }`}
        >
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
            <Smartphone size={24} className={formData.reminderMode === 'manual' ? 'text-blue-600' : 'text-blue-500'} />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">Semi-automático</h3>
          <p className="text-sm text-slate-600 mb-3">
            Você clica para enviar. O sistema prepara a mensagem pronta.
          </p>
          <div className="flex items-center gap-2 text-sm">
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              Sem custo extra
            </span>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            <li className="flex items-center gap-2">
              <Check size={14} className="text-blue-500" /> Lista de lembretes do dia
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} className="text-blue-500" /> 1 clique para enviar
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} className="text-blue-500" /> Abre WhatsApp pronto
            </li>
          </ul>
        </button>
      </div>

      {/* Conteúdo baseado na escolha */}
      {formData.reminderMode === 'automatic' && (
        <>
          {/* Explicação do Z-API */}
          <div className="bg-green-50 rounded-2xl p-6 border border-green-100 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
                <MessageCircle size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-green-900 mb-2">Como funciona o Z-API?</h3>
                <p className="text-green-800 text-sm">
                  Você cria uma conta no Z-API (R$ 99/mês), conecta seu WhatsApp, e nos passa as credenciais. 
                  Pronto! O sistema envia os lembretes automaticamente.
                </p>
              </div>
            </div>
          </div>

          {/* Passo a passo */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Smartphone size={20} className="text-blue-600" />
              Como criar sua conta Z-API
            </h3>
            
            <ol className="space-y-4">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                <div>
                  <p className="text-slate-800">Acesse o site do Z-API e crie sua conta</p>
                  <a 
                    href="https://www.z-api.io" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm mt-1"
                  >
                    <ExternalLink size={14} />
                    www.z-api.io
                  </a>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                <div>
                  <p className="text-slate-800">Escolha um plano (R$ 99/mês)</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                <div>
                  <p className="text-slate-800">Conecte seu WhatsApp escaneando o QR Code</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                <div>
                  <p className="text-slate-800">Copie o <strong>Instance ID</strong> e o <strong>Token</strong></p>
                </div>
              </li>
            </ol>

            {/* Imagem ilustrativa */}
            <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-xs text-slate-500 mb-2 text-center">Onde encontrar suas credenciais:</p>
              <div className="bg-white rounded-lg p-4 border border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <span className="text-xs text-slate-400 ml-2">Painel Z-API</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <span className="text-slate-600">Instance ID:</span>
                    <span className="font-mono text-blue-600 bg-blue-100 px-2 py-0.5 rounded">3D1A2B3C4D5E</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                    <span className="text-slate-600">Token:</span>
                    <span className="font-mono text-green-600 bg-green-100 px-2 py-0.5 rounded text-xs">abc123xyz...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulário de credenciais */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">Cole suas credenciais aqui</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="zapiInstanceId">Instance ID</Label>
                <Input
                  id="zapiInstanceId"
                  name="zapiInstanceId"
                  value={formData.zapiInstanceId}
                  onChange={handleInputChange}
                  placeholder="Ex: 3D1A2B3C4D5E6F7G"
                  className="mt-2 font-mono"
                />
              </div>
              <div>
                <Label htmlFor="zapiToken">Token</Label>
                <Input
                  id="zapiToken"
                  name="zapiToken"
                  value={formData.zapiToken}
                  onChange={handleInputChange}
                  placeholder="Ex: abc123xyz456def789"
                  className="mt-2 font-mono"
                />
              </div>
            </div>
            
            <div className="mt-4 flex items-center gap-2">
              <input
                type="checkbox"
                id="skipZapiCheck"
                checked={formData.skipZapi}
                onChange={(e) => setFormData({ ...formData, skipZapi: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300"
              />
              <label htmlFor="skipZapiCheck" className="text-sm text-slate-600">
                Vou configurar o Z-API depois
              </label>
            </div>
          </div>

          {/* URLs de Webhook */}
          {formData.zapiInstanceId && formData.zapiToken && (
            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6 mb-6">
              <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                <Bell size={18} />
                Último passo: Configure os Webhooks no Z-API
              </h3>
              <p className="text-sm text-amber-800 mb-4">
                Cole estas URLs no painel do Z-API para receber confirmações automáticas:
              </p>
              
              {/* Imagem de exemplo do painel Z-API */}
              <div className="mb-4 p-4 bg-white rounded-xl border border-amber-200">
                <p className="text-xs text-amber-600 mb-3 text-center font-medium">📍 Onde colar no painel Z-API:</p>
                <div className="bg-slate-900 rounded-lg p-4 text-white text-sm">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-slate-400 text-xs">Webhooks - Z-API</span>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Ao receber</label>
                      <div className="bg-slate-800 rounded px-3 py-2 text-green-400 text-xs font-mono break-all">
                        https://www.ativaja.com/api/zapi/webhook/{slug}/receive
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Receber status da mensagem</label>
                      <div className="bg-slate-800 rounded px-3 py-2 text-green-400 text-xs font-mono break-all">
                        https://www.ativaja.com/api/zapi/webhook/{slug}/status
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* URLs para copiar */}
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-3 border border-amber-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-amber-600 mb-1">Ao receber (mensagens):</p>
                      <code className="text-sm text-slate-800 break-all">
                        https://www.ativaja.com/api/zapi/webhook/{slug}/receive
                      </code>
                    </div>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(`https://www.ativaja.com/api/zapi/webhook/${slug}/receive`);
                      }}
                      className="p-2 hover:bg-amber-100 rounded-lg transition-colors"
                      title="Copiar"
                    >
                      <Copy size={16} className="text-amber-600" />
                    </button>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-amber-600 mb-1">Receber status:</p>
                      <code className="text-sm text-slate-800 break-all">
                        https://www.ativaja.com/api/zapi/webhook/{slug}/status
                      </code>
                    </div>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(`https://www.ativaja.com/api/zapi/webhook/${slug}/status`);
                      }}
                      className="p-2 hover:bg-amber-100 rounded-lg transition-colors"
                      title="Copiar"
                    >
                      <Copy size={16} className="text-amber-600" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-green-100 rounded-lg border border-green-200">
                <p className="text-sm text-green-800 font-medium">
                  ✅ Salve e pronto! Está configurado para uso.
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Quando um cliente responder SIM ou NÃO, o agendamento será atualizado automaticamente.
                </p>
              </div>
            </div>
          )}

          {/* Botão de ajuda */}
          <button
            onClick={handleOpenSupport}
            className="w-full p-4 rounded-xl border-2 border-green-200 bg-green-50 text-left hover:border-green-300 transition-all mb-6"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                <HelpCircle size={20} className="text-white" />
              </div>
              <div>
                <p className="font-medium text-green-800">Preciso de ajuda para configurar</p>
                <p className="text-sm text-green-600">Falar com suporte no WhatsApp</p>
              </div>
            </div>
          </button>
        </>
      )}

      {formData.reminderMode === 'manual' && (
        <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
              <MessageCircle size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Como vai funcionar</h3>
              <p className="text-blue-800 text-sm mb-4">
                Todo dia, você verá uma lista dos clientes que precisam receber lembrete. 
                É só clicar no botão e o WhatsApp abre com a mensagem pronta!
              </p>
              <div className="bg-white rounded-xl p-4 border border-blue-200">
                <p className="text-xs text-slate-500 mb-3">Exemplo do seu painel:</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-800 text-sm">João Silva</p>
                      <p className="text-xs text-slate-500">Corte às 14:00</p>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium">
                      <MessageCircle size={14} />
                      Enviar
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-800 text-sm">Maria Santos</p>
                      <p className="text-xs text-slate-500">Barba às 15:30</p>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium">
                      <MessageCircle size={14} />
                      Enviar
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(4)}>
          <ArrowLeft size={18} />
          Voltar
        </Button>
        <Button 
          onClick={handleFinish} 
          className="btn-primary"
          disabled={!formData.reminderMode}
        >
          <Save size={18} />
          Finalizar configuração
        </Button>
      </div>
    </div>
  );

  // Step 6: Sucesso
  const renderStep6 = () => (
    <div className="max-w-xl mx-auto text-center">
      <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
        <Check size={40} className="text-green-600" />
      </div>
      
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Configuração concluída!</h2>
      <p className="text-slate-600 mb-8">
        Sua agenda está pronta para ser ativada.
      </p>
      
      {/* Prévia da página */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 mb-6 border border-slate-200">
        <p className="text-sm text-slate-600 mb-4 font-medium">📱 Veja como sua página ficou:</p>
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200 mb-4">
          {/* Mini browser frame */}
          <div className="bg-slate-100 px-3 py-2 flex items-center gap-2 border-b border-slate-200">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
            </div>
            <div className="flex-1 bg-white rounded px-3 py-1 text-xs text-slate-500 font-mono truncate">
              {fullUrl}
            </div>
          </div>
          
          {/* Preview content */}
          <div className="p-4 text-left">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-bold text-slate-900">{formData.business || 'Seu Negócio'}</h4>
                <p className="text-xs text-slate-500">Agendamento online</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <MessageCircle size={14} className="text-green-600" />
              </div>
            </div>
            
            <p className="text-xs text-slate-600 mb-3">Escolha o serviço:</p>
            <div className="space-y-2">
              {formData.services.slice(0, 3).map((service, i) => (
                <div key={i} className="flex justify-between items-center p-2 bg-slate-50 rounded-lg text-xs">
                  <span className="text-slate-700">{service.name}</span>
                  <span className="text-blue-600 font-semibold">{formatPrice(service.price)}</span>
                </div>
              ))}
              {formData.services.length > 3 && (
                <p className="text-xs text-slate-400 text-center">+{formData.services.length - 3} serviços</p>
              )}
            </div>
          </div>
        </div>
        
        <Button 
          onClick={() => setShowPreview(true)} 
          variant="outline" 
          className="w-full"
          data-testid="preview-full-btn"
        >
          <Eye size={18} />
          Ver página completa
        </Button>
      </div>

      {/* Botões de ação */}
      <div className="flex gap-3 mb-6">
        <Button 
          onClick={() => navigate(`/painel/${slug}`)} 
          className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
        >
          <Calendar size={18} />
          Acessar meu painel
        </Button>
        <Button 
          onClick={() => navigate(`/agenda/${slug}`)} 
          variant="outline"
          className="flex-1"
        >
          <Eye size={18} />
          Ver minha página
        </Button>
      </div>

      {/* Link para copiar */}
      <div className="bg-slate-50 rounded-xl p-4 mb-6">
        <p className="text-sm text-slate-500 mb-2">Seu link exclusivo:</p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={fullUrl}
            readOnly
            className="flex-1 px-4 py-3 bg-white rounded-lg border border-slate-200 text-slate-800 text-sm"
          />
          <Button onClick={handleCopyLink} variant="outline" data-testid="copy-link-btn">
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </Button>
        </div>
      </div>
      
      {/* Box de ativação */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
          <Calendar size={24} className="text-white" />
        </div>
        <h3 className="text-xl font-bold mb-2">Ative sua agenda agora</h3>
        <p className="text-blue-100 text-sm mb-6">
          Para liberar seu link e receber lembretes automáticos no WhatsApp, 
          ative sua assinatura.
        </p>
        
        <div className="bg-white/10 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-blue-100">Assinatura mensal</span>
            <span className="text-2xl font-bold">R$ 49,99</span>
          </div>
          <ul className="text-left text-sm text-blue-100 space-y-1">
            <li className="flex items-center gap-2">
              <Check size={14} /> Agendamentos ilimitados
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} /> Lembretes WhatsApp automáticos
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} /> Confirmação com 1 clique
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} /> Painel de controle
            </li>
          </ul>
        </div>
        
        {paymentLink ? (
          <div className="space-y-3">
            <a
              href={paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-green-500 hover:bg-green-400 text-white font-semibold py-3 px-6 rounded-full inline-flex items-center justify-center gap-2 transition-colors"
            >
              <ExternalLink size={18} />
              Pagar agora - R$ 49,99
            </a>
            <p className="text-xs text-green-200">
              ✓ Link de pagamento gerado com sucesso!
            </p>
          </div>
        ) : (
          <Button
            className="w-full bg-white text-blue-600 hover:bg-blue-50 font-semibold py-3 disabled:opacity-70"
            onClick={handleCreatePaymentLink}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Gerando link...
              </>
            ) : (
              <>
                <Zap size={18} />
                Gerar link de pagamento
              </>
            )}
          </Button>
        )}
        
        {paymentError && (
          <p className="text-xs text-red-200 mt-3">
            Erro: {paymentError}
          </p>
        )}
        
        <p className="text-xs text-blue-200 mt-4">
          Pagamento via Pix, cartão ou boleto • Ativação automática
        </p>
      </div>
      
      <div className="mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <p className="text-sm text-amber-800">
          <strong>💡 Modo teste:</strong> Você pode visualizar e testar sua agenda agora. 
          Os lembretes serão ativados após o pagamento.
        </p>
      </div>

      {/* Status do modo de lembrete */}
      {formData.reminderMode === 'automatic' && (
        <>
          {formData.skipZapi ? (
            <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>⏳ Modo Automático:</strong> Você escolheu lembretes automáticos, mas ainda precisa configurar o Z-API. 
                <button 
                  onClick={() => setStep(5)} 
                  className="text-blue-600 hover:underline ml-1"
                >
                  Configurar agora
                </button>
              </p>
            </div>
          ) : formData.zapiInstanceId && formData.zapiToken ? (
            <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
              <p className="text-sm text-green-700">
                <strong>✓ Modo Automático:</strong> Z-API configurado! Os lembretes serão enviados automaticamente após o pagamento.
              </p>
            </div>
          ) : (
            <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>⏳ Modo Automático:</strong> Preencha as credenciais do Z-API para ativar. 
                <button 
                  onClick={() => setStep(5)} 
                  className="text-blue-600 hover:underline ml-1"
                >
                  Configurar agora
                </button>
              </p>
            </div>
          )}
        </>
      )}
      
      {formData.reminderMode === 'manual' && (
        <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>✓ Modo Semi-automático:</strong> Você verá uma lista de lembretes para enviar com 1 clique. 
            Sem custo extra!
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Modal de Preview */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 flex items-center justify-between border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="bg-white rounded px-3 py-1 text-sm text-slate-500 font-mono">
                  {fullUrl}
                </div>
              </div>
              <button 
                onClick={() => setShowPreview(false)}
                className="p-1 hover:bg-slate-200 rounded transition-colors"
              >
                <X size={20} className="text-slate-500" />
              </button>
            </div>
            <iframe
              src={`/agenda/${slug}`}
              className="w-full h-[70vh]"
              title="Preview da sua agenda"
            />
            <div className="bg-slate-50 px-4 py-3 flex justify-between items-center border-t border-slate-200">
              <p className="text-sm text-slate-600">Esta é uma prévia de como seus clientes verão sua página</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Fechar
                </Button>
                <Button onClick={() => window.open(`/agenda/${slug}`, '_blank')} className="btn-primary">
                  <ExternalLink size={16} />
                  Abrir em nova aba
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={siteData.brand.logo} alt="AtivaJá" className="h-8" />
            <span className="text-slate-400">|</span>
            <span className="font-medium text-slate-700">Agenda Cheia</span>
          </a>
          
          {step < 6 && (
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <div
                  key={s}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    s === step
                      ? 'bg-blue-600 text-white'
                      : s < step
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {s < step ? <Check size={16} /> : s}
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="py-12 px-4">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
        {step === 6 && renderStep6()}
      </main>

      {/* Master Access */}
      <MasterAccess 
        produto="agenda" 
        slug={slug}
        onSuccess={() => navigate(`/agenda/painel/${slug}`)}
      />
    </div>
  );
};

export default AgendaConfigurar;
